import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { getDisplayName, getFriendDisplayName } from '../../utils/displayName';
import { useTranslation } from 'react-i18next';
import Logo from '../../components/Logo';
import BottomNav from '../../components/BottomNav';
import FlagIcon from '../../components/FlagIcon';
import Avatar from '../../components/Avatar';
import TempProfileBanner from '../../components/TempProfileBanner';
import PushPromptBanner from '../../components/PushPromptBanner';
import ConfirmSheet from '../../components/ConfirmSheet';
import BlockedFriendsSheet from '../../components/BlockedFriendsSheet';
import DeletedFriendsSheet from '../../components/DeletedFriendsSheet';
import Toast from '../../components/Toast';
import { supabase } from '../../supabaseClient';
import { resolveAvatarUrl } from '../../utils/imageUrl';
import { useNotification } from '../../contexts/NotificationContext';
import { useUnreadNotificationCheck } from '../../hooks/useUnreadNotificationCheck';
import { getMessagePreview } from '../../utils/messagePreview';
import { getListCache, setListCache, invalidateListCache } from '../../utils/listCache';
const formatChatListTime = (dateString, t) => {
  if (!dateString) return { dateText: '', timeText: '' };
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return { dateText: '', timeText: '' };

  const hours = String(date.getHours()).padStart(2, '0');
  const minStr = String(date.getMinutes()).padStart(2, '0');
  const timeStr = `${hours}:${minStr}`;

  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isToday) {
    return { dateText: t('chat_list.date_today'), timeText: timeStr };
  }

  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  if (isYesterday) {
    return { dateText: t('chat_list.date_yesterday', '어제'), timeText: timeStr };
  }

  const isThisYear = date.getFullYear() === now.getFullYear();

  if (isThisYear) {
    return {
      dateText: t('chat_list.date_format', { month: date.getMonth() + 1, day: date.getDate() }),
      timeText: timeStr,
    };
  }

  return {
    dateText: `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`,
    timeText: timeStr,
  };
};

export default function ChatList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { notifSignal } = useNotification();
  const [isFabExpanded, setIsFabExpanded] = useState(true);
  const [toastConfig, setToastConfig] = useState(null);

  useEffect(() => {
    if (location.state?.toast) {
      setToastConfig({
        message: location.state.toast,
        duration: location.state.duration || 2500,
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const [userId, setUserId] = useState(null);
  const hasUnreadNotifications = useUnreadNotificationCheck(userId);
  const [nameSet, setNameSet] = useState(null);
  const [myLanguage, setMyLanguage] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isBlockConfirmOpen, setIsBlockConfirmOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBlockedSheetOpen, setIsBlockedSheetOpen] = useState(false);
  const [isDeletedSheetOpen, setIsDeletedSheetOpen] = useState(false);
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInviteChat, setSelectedInviteChat] = useState(null);
  const [isProcessingInvite, setIsProcessingInvite] = useState(false);
  const debounceTimerRef = useRef(null);
  const messagesDebounceTimerRef = useRef(null);
  const profilesDebounceTimerRef = useRef(null);
  const prevNotifSignalRef = useRef(notifSignal);
  const chatsRef = useRef([]);

  const fetchFavorites = async (uid) => {
    if (!uid) {
      setFavorites([]);
      return;
    }
    const { data: friendsData, error: friendsError } = await supabase
      .from('friendships')
      .select('friend_id')
      .eq('user_id', uid)
      .eq('status', 'active');

    if (friendsError) {
      console.error('Error fetching friendships:', friendsError);
    } else if (friendsData) {
      setFavorites(friendsData.map(f => f.friend_id));
    }
  };

  const fetchMyProfile = async (uid) => {
    if (!uid) {
      setNameSet(null);
      setMyLanguage(null);
      return;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('name_set, my_language')
      .eq('id', uid)
      .maybeSingle();

    if (!error && data) {
      setNameSet(data.name_set ?? false);
      setMyLanguage(data.my_language ?? null);
    }
  };

  useEffect(() => {
    const initUserData = async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;

      if (sessionError || !currentUserId) {
        setUserId(null);
        setNameSet(null);
        setMyLanguage(null);
        setFavorites([]);
        return;
      }

      setUserId(currentUserId);

      // [로컬 캐시 사본 선표시 — B안] 사용자 ID 획득 직후 사본 주입
      const cached = getListCache(currentUserId, 'chat_list');
      if (Array.isArray(cached) && cached.length > 0) {
        setChats(prev => (prev.length === 0 ? cached : prev));
        setIsLoading(false);
      }

      await fetchFavorites(currentUserId);
      await fetchMyProfile(currentUserId);
    };

    initUserData();
  }, []);

  // 화면 재진입 시(location 변경 등) 본인 프로필(name_set) 재조회
  useEffect(() => {
    if (userId) {
      fetchMyProfile(userId);
    }
  }, [location.key, userId]);

  const toggleFavorite = async (e, partnerId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!userId || !partnerId) return;

    const isFavorite = favorites.includes(partnerId);
    const prevFavorites = [...favorites];

    setFavorites(prev =>
      isFavorite ? prev.filter(id => id !== partnerId) : [...prev, partnerId]
    );

    try {
      if (isFavorite) {
        const { error } = await supabase
          .from('friendships')
          .delete()
          .eq('user_id', userId)
          .eq('friend_id', partnerId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('friendships')
          .insert({
            user_id: userId,
            friend_id: partnerId,
            status: 'active'
          });

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error toggling favorite friendship:', error);
      setFavorites(prevFavorites);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFabExpanded(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);
  const [hasGroupNotification, setHasGroupNotification] = useState(false);

  const checkGroupNotifications = async (currentUserId) => {
    const targetUserId = currentUserId || userId;
    if (!targetUserId) {
      setHasGroupNotification(false);
      return;
    }

    try {
      // 1. 내 참여 정보 조회 (active 또는 pending, hidden: false)
      const { data: myParts, error: partsError } = await supabase
        .from('participants')
        .select('conversation_id, status')
        .eq('profile_id', targetUserId)
        .eq('hidden', false);

      const activeConvIds = [];
      const pendingConvIds = [];
      (myParts || []).forEach((p) => {
        if (p.status === 'active') activeConvIds.push(p.conversation_id);
        else if (p.status === 'pending') pendingConvIds.push(p.conversation_id);
      });

      const allMyConvIds = [...activeConvIds, ...pendingConvIds];

      if (allMyConvIds.length > 0) {
        // 2. 단체 대화방(type === 'group') 메타 1회 단일 조회
        const { data: groupConvs, error: convError } = await supabase
          .from('conversations')
          .select('id')
          .eq('type', 'group')
          .in('id', allMyConvIds);

        if (!convError && groupConvs && groupConvs.length > 0) {
          const groupConvIdSet = new Set(groupConvs.map((c) => c.id));

          // (나) 응답하지 않은 단체방 초대(pending)가 있는지 확인
          const hasPendingGroup = pendingConvIds.some((id) => groupConvIdSet.has(id));
          if (hasPendingGroup) {
            setHasGroupNotification(true);
            return;
          }

          // (가) 참여 중(active)인 단체방 중 안 읽은 메시지가 있는지 확인
          const activeGroupConvIds = activeConvIds.filter((id) => groupConvIdSet.has(id));
          if (activeGroupConvIds.length > 0) {
            const { count: msgCount, error: msgError } = await supabase
              .from('messages')
              .select('id', { count: 'exact', head: true })
              .in('conversation_id', activeGroupConvIds)
              .neq('sender_id', targetUserId)
              .eq('is_read', false);

            if (!msgError && typeof msgCount === 'number' && msgCount > 0) {
              // 남이 남에게 보낸 선물 카드는 내 알림 대상이 아니므로 차감한다
              // (GroupChatList.jsx 의 배지 계산과 동일한 기준을 적용)
              let othersGiftCnt = 0;
              const { count: giftCnt, error: giftErr } = await supabase
                .from('messages')
                .select('id', { count: 'exact', head: true })
                .in('conversation_id', activeGroupConvIds)
                .neq('sender_id', targetUserId)
                .eq('is_read', false)
                .eq('message_type', 'system')
                .eq('system_payload->>event', 'gift_sent')
                .neq('system_payload->receiver->>id', targetUserId);

              if (giftErr) {
                console.error('남의 선물 카드 카운트 조회 실패:', giftErr);
              } else {
                othersGiftCnt = giftCnt || 0;
              }

              // 내가 보낸 선물이 아닌 다른 사람의 선물 거절 메시지도 내 알림 대상이 아니므로 차감한다
              let othersGiftRejectedCnt = 0;
              const { count: rejCnt, error: rejErr } = await supabase
                .from('messages')
                .select('id', { count: 'exact', head: true })
                .in('conversation_id', activeGroupConvIds)
                .neq('sender_id', targetUserId)
                .eq('is_read', false)
                .eq('message_type', 'system')
                .eq('system_payload->>event', 'gift_rejected')
                .neq('system_payload->sender->>id', targetUserId);

              if (rejErr) {
                console.error('남의 선물 거절 메시지 카운트 조회 실패:', rejErr);
              } else {
                othersGiftRejectedCnt = rejCnt || 0;
              }

              // 내가 보낸 선물이 아닌 다른 사람의 선물 수령 메시지도 내 알림 대상이 아니므로 차감한다
              let othersGiftClaimedCnt = 0;
              const { count: claimCnt, error: claimErr } = await supabase
                .from('messages')
                .select('id', { count: 'exact', head: true })
                .in('conversation_id', activeGroupConvIds)
                .neq('sender_id', targetUserId)
                .eq('is_read', false)
                .eq('message_type', 'system')
                .eq('system_payload->>event', 'gift_claimed')
                .neq('system_payload->sender->>id', targetUserId);

              if (claimErr) {
                console.error('남의 선물 수령 메시지 카운트 조회 실패:', claimErr);
              } else {
                othersGiftClaimedCnt = claimCnt || 0;
              }

              if (Math.max(0, msgCount - othersGiftCnt - othersGiftRejectedCnt - othersGiftClaimedCnt) > 0) {
                setHasGroupNotification(true);
                return;
              }
            }
          }
          // (다) notifications 테이블에 내가 받은 안 읽은 초대 응답 알림(수락/거절)이 있는지 확인
          // (내가 현재 참여 중이거나 초대 대기 중인 유효 단체방의 알림으로 범위 한정하여 나간 방 유령 점등 차단)
          const pendingGroupConvIds = pendingConvIds.filter((id) => groupConvIdSet.has(id));
          const targetGroupConvIds = [...activeGroupConvIds, ...pendingGroupConvIds];

          if (targetGroupConvIds.length > 0) {
            const { count: notifCount, error: notifError } = await supabase
              .from('notifications')
              .select('id', { count: 'exact', head: true })
              .eq('recipient_id', targetUserId)
              .eq('is_read', false)
              .in('type', ['group_invite_accepted', 'group_invite_rejected'])
              .in('conversation_id', targetGroupConvIds);

            if (!notifError && typeof notifCount === 'number' && notifCount > 0) {
              setHasGroupNotification(true);
              return;
            }
          }
        }
      }

      setHasGroupNotification(false);
    } catch (err) {
      console.error('Error checking group notifications:', err);
      setHasGroupNotification(false);
    }
  };

  // [폴백 전용] RPC 실패 시에만 호출되는 기존 폭포수 조회 경로. 수정 금지.
  const fetchChatsLegacy = async () => {
    if (!userId) {
      setChats([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    // [작업 1-2] 내 참여 행 조회 시 status 컬럼 포함 (hidden = false 유지)
    const { data: myParticipants, error: myError } = await supabase
      .from('participants')
      .select('conversation_id, status')
      .eq('profile_id', userId)
      .eq('hidden', false);
    
    if (myError) {
      console.error('Error fetching my participants:', myError);
      setIsLoading(false);
      return;
    }

    // [3-E 작업 1] active 및 pending 상태인 대화방 목록 대상 포함 (rejected 제외)
    const activeOrPendingMyParticipants = (myParticipants || []).filter(
      p => p.status === 'active' || p.status === 'pending'
    );
    const conversationIds = activeOrPendingMyParticipants.map(p => p.conversation_id);
    
    if (conversationIds.length === 0) {
      setChats([]);
      setIsLoading(false);
      return;
    }

    const myStatusMap = new Map();
    (myParticipants || []).forEach(p => {
      myStatusMap.set(p.conversation_id, p.status || 'active');
    });

    // [작업 1-1] 대화방 메타 정보(type, name, created_at) 1회 단일 쿼리 조회 (.in 사용)
    const convMap = new Map();
    const { data: convData, error: convError } = await supabase
      .from('conversations')
      .select('id, type, name, created_at')
      .in('id', conversationIds);

    if (convError) {
      console.warn('Error fetching conversations meta, fallback to direct:', convError);
    } else if (convData) {
      convData.forEach(c => {
        convMap.set(c.id, c);
      });
    }

    const { data: blockedRows, error: blockedError } = await supabase
      .from('friendships')
      .select('friend_id')
      .eq('user_id', userId)
      .eq('status', 'blocked');

    if (blockedError) {
      console.error('Error fetching blocked friends:', blockedError);
    }
    const blockedIds = new Set((blockedRows || []).map(r => r.friend_id));

    // 상대 참여자 목록 조회 (status 포함)
    const { data: otherParticipants, error: otherError } = await supabase
      .from('participants')
      .select(`
        conversation_id,
        status,
        profiles!participants_profile_id_fkey ( id, name, flag_code, my_language, avatar, avatar_selected, nickname, name_display_choice )
      `)
      .in('conversation_id', conversationIds)
      .neq('profile_id', userId);
    
    if (otherError) {
      console.error('Error fetching other participants:', otherError);
    }

    // [작업 2] 초대를 수락한 active 상태인 상대 참여자만 conversation_id 별로 그룹핑
    const othersByConvId = new Map();
    (otherParticipants || []).forEach(p => {
      if (p.status !== 'active') return;
      if (!othersByConvId.has(p.conversation_id)) {
        othersByConvId.set(p.conversation_id, []);
      }
      if (p.profiles) {
        othersByConvId.get(p.conversation_id).push(p.profiles);
      }
    });

    // 친구 별칭 1회 단일 배치 조회
    const allOtherProfileIds = (otherParticipants || []).map(p => p.profiles?.id).filter(Boolean);
    const nicknameMap = new Map();
    if (allOtherProfileIds.length > 0) {
      const { data: fData, error: fError } = await supabase
        .from('friendships')
        .select('friend_id, nickname')
        .eq('user_id', userId)
        .in('friend_id', allOtherProfileIds);
        
      if (!fError && fData) {
        fData.forEach(f => {
          if (f.nickname) {
            nicknameMap.set(f.friend_id, f.nickname);
          }
        });
      }
    }

    // [작업 2] 대화방 ID 기준으로 1:1 대응하여 목록 포맷팅
    const formattedData = await Promise.all(
      conversationIds.map(async (convId) => {
        const convMeta = convMap.get(convId);
        const convType = convMeta?.type || 'direct';
        const others = othersByConvId.get(convId) || [];
        const roomName = convMeta?.name || null;
        const myStatus = myStatusMap.get(convId) || 'active';
        const memberCount = others.length + (myStatus === 'active' ? 1 : 0);

        let lastMessage = '';
        let dateText = '';
        let timeText = '';
        let unread = false;
        let hasMessages = false;
        let isRead = false;
        let senderId = null;
        let unreadCount = 0;
        let lastMsgTimestamp = convMeta?.created_at ? new Date(convMeta.created_at).getTime() : 0;

        // [3-E 작업 2] pending 상태인 방과 active 상태인 방 분기
        if (myStatus !== 'pending') {
          const { data: latestMsgs, error: msgError } = await supabase
            .from('messages')
            .select('original_text, translated_text, created_at, is_read, sender_id, message_type, system_payload, target_language, target_language_2, translated_text_2')
            .eq('conversation_id', convId)
            .order('created_at', { ascending: false })
            .limit(1);

          if (!msgError && latestMsgs && latestMsgs.length > 0) {
            const msg = latestMsgs[0];
            hasMessages = true;
            lastMessage = getMessagePreview(msg, userId, t, { myLanguage });
            const formatted = formatChatListTime(msg.created_at, t);
            dateText = formatted.dateText;
            timeText = formatted.timeText;
            unread = msg.sender_id !== userId && msg.is_read === false;
            isRead = msg.is_read;
            senderId = msg.sender_id;
            if (msg.created_at) {
              lastMsgTimestamp = new Date(msg.created_at).getTime();
            }
          } else {
            lastMessage = getMessagePreview(null, userId, t);
          }

          const { count: unreadCnt } = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('conversation_id', convId)
            .neq('sender_id', userId)
            .eq('is_read', false);

          unreadCount = unreadCnt || 0;
        } else {
          // myStatus === 'pending' 상태인 방
          if (convType === 'direct') {
            // (4-1) 1:1 채팅 pending: 해당 방의 가장 오래된 메시지 1건만 조회 (created_at 오름차순 limit 1)
            const { data: firstMsgs, error: firstMsgError } = await supabase
              .from('messages')
              .select('original_text, translated_text, created_at, is_read, sender_id, message_type, system_payload, target_language, target_language_2, translated_text_2')
              .eq('conversation_id', convId)
              .order('created_at', { ascending: true })
              .limit(1);

            const partnerProfile = others[0] || null;
            const partnerId = partnerProfile?.id || null;

            if (!firstMsgError && firstMsgs && firstMsgs.length > 0) {
              const firstMsg = firstMsgs[0];
              if (firstMsg && partnerId && firstMsg.sender_id === partnerId) {
                lastMessage = getMessagePreview(firstMsg, userId, t, { myLanguage });
              } else {
                lastMessage = t('common.preview_invite_pending');
              }
            } else {
              lastMessage = t('common.preview_invite_pending');
            }
          } else {
            // (4-2) 단체 대화방 pending: 미리보기 일절 차단
            lastMessage = t('common.preview_invite_pending');
          }
        }

        if (convType === 'direct') {
          // [작업 2-3] 1:1 대화방 (기존과 100% 동일한 값과 필드 구조)
          const partnerProfile = others[0] || null;
          const partnerFriendNickname = partnerProfile ? (nicknameMap.get(partnerProfile.id) || null) : null;
          return {
            id: convId,
            type: 'direct',
            memberCount: 2,
            roomName: null,
            myStatus,
            partnerId: partnerProfile?.id || null,
            name: partnerProfile ? getFriendDisplayName(partnerFriendNickname, partnerProfile) : t('chat_list.unknown_partner', '대화 상대'),
            flagCode: partnerProfile?.flag_code || null,
            language: partnerProfile?.my_language || null,
            avatar: resolveAvatarUrl(partnerProfile?.avatar),
            avatarSelected: partnerProfile?.avatar_selected ?? false,
            avatars: [resolveAvatarUrl(partnerProfile?.avatar)],
            partnerProfile: partnerProfile ? {
              id: partnerProfile.id,
              name: partnerProfile.name,
              nickname: partnerProfile.nickname,
              name_display_choice: partnerProfile.name_display_choice,
              friendNickname: partnerFriendNickname,
              flagCode: partnerProfile.flag_code || null,
              myLanguage: partnerProfile.my_language || null,
              avatar: resolveAvatarUrl(partnerProfile.avatar),
              avatarSelected: partnerProfile.avatar_selected ?? false
            } : null,
            lastMessage,
            dateText,
            timeText,
            unread,
            hasMessages,
            isRead,
            senderId,
            unreadCount,
            lastMsgTimestamp,
            isBlocked: partnerProfile ? blockedIds.has(partnerProfile.id) : false,
          };
        } else {
          // [작업 2-4] 단체 대화방 (active 참여자 기준 이름 및 아바타 스택)
          let groupName = roomName;
          if (!groupName) {
            const names = others.map(p => getFriendDisplayName(nicknameMap.get(p.id), p));
            groupName = names.length > 0 ? names.join(', ') : t('chat_list.group_chat_default_name', '단체 대화방');
          }
          const groupAvatars = others.slice(0, 3).map(p => resolveAvatarUrl(p.avatar));

          return {
            id: convId,
            type: 'group',
            memberCount,
            roomName,
            myStatus,
            partnerId: null,
            otherProfileIds: others.map(p => p.id),
            name: groupName,
            flagCode: null,
            language: null,
            avatar: groupAvatars[0] || resolveAvatarUrl(null),
            avatars: groupAvatars,
            lastMessage,
            dateText,
            timeText,
            unread,
            hasMessages,
            isRead,
            senderId,
            unreadCount,
            lastMsgTimestamp,
            isBlocked: false,
          };
        }
      })
    );

    // [3-E 작업 3] 목록 정렬: pending 초대 방을 최상단에 배치, 나머지는 최신 메시지 시각순 정렬
    formattedData.sort((a, b) => {
      if (a.myStatus === 'pending' && b.myStatus !== 'pending') return -1;
      if (a.myStatus !== 'pending' && b.myStatus === 'pending') return 1;
      const timeA = a.lastMsgTimestamp || 0;
      const timeB = b.lastMsgTimestamp || 0;
      return timeB - timeA;
    });

    setChats(formattedData);
    setIsLoading(false);
    if (Array.isArray(formattedData) && formattedData.length > 0) {
      setListCache(userId, 'chat_list', formattedData);
    }
  };

  /**
   * [어댑터] RPC get_my_chat_list 반환 배열을 UI 요구 규격 객체 배열로 변환하는 순수 메모리 함수
   * @param {Array} rpcList - RPC 반환 JSONB 객체 배열
   * @param {string} myUserId - 현재 로그인한 사용자 ID
   * @param {Function} translator - i18n 번역 함수
   * @returns {Array} UI 요구 규격 객체 배열
   */
  const adaptRpcChatList = (rpcList, myUserId, translator, userLanguage) => {
    if (!Array.isArray(rpcList)) return [];

    return rpcList.map((chat) => {
      // 1. 마지막 메시지 미리보기 생성 (기존 분기 로직과 100% 동일)
      let lastMessage = '';
      if (chat.myStatus === 'pending') {
        if (chat.type === 'direct') {
          if (
            chat.rawLastMessage &&
            chat.partnerId &&
            chat.rawLastMessage.sender_id === chat.partnerId
          ) {
            lastMessage = getMessagePreview(chat.rawLastMessage, myUserId, translator, { myLanguage: userLanguage });
          } else {
            lastMessage = translator('common.preview_invite_pending');
          }
        } else {
          lastMessage = translator('common.preview_invite_pending');
        }
      } else {
        lastMessage = getMessagePreview(chat.rawLastMessage, myUserId, translator, { myLanguage: userLanguage });
      }

      // 2. 날짜 / 시간 텍스트 생성 (rawLastMessage?.created_at 없을 시 빈 문자열 반환)
      const formatted = formatChatListTime(chat.rawLastMessage?.created_at, translator);
      const dateText = formatted.dateText;
      const timeText = formatted.timeText;

      // 3. 아바타 목록 배열 조립 (1:1 및 단체 모두 유효한 배열 보장)
      const resolvedAvatar = resolveAvatarUrl(chat.avatar);
      const avatars = resolvedAvatar ? [resolvedAvatar] : [];

      // 4. 언어 설정 매핑 (target.language 참조용)
      const language = chat.partnerProfile?.myLanguage || null;

      // 5. otherProfileIds 항상 배열 보장 (L767 Array.isArray 가드 및 .includes 호출 안전)
      const otherProfileIds = Array.isArray(chat.otherProfileIds) ? chat.otherProfileIds : [];

      return {
        // RPC 16개 필드 그대로 통과
        id: chat.id,
        type: chat.type,
        roomName: chat.roomName,
        myStatus: chat.myStatus,
        partnerId: chat.partnerId,
        otherProfileIds,
        name: chat.name,
        flagCode: chat.flagCode,
        avatar: resolvedAvatar,
        avatarSelected: chat.avatarSelected ?? false,
        partnerProfile: chat.partnerProfile,
        unreadCount: chat.unreadCount ?? 0,
        unread: chat.unread ?? false,
        hasMessages: chat.hasMessages ?? false,
        lastMsgTimestamp: chat.lastMsgTimestamp ?? 0,
        isBlocked: chat.isBlocked ?? false,
        memberCount: chat.memberCount ?? 2,

        // 어댑터 생성 4개 필드
        lastMessage,
        dateText,
        timeText,
        avatars,
        language,
      };
    });
  };

  // [1차 경로] 단일 RPC 호출 기반 채팅목록 조회 (실패 시 fetchChatsLegacy로 자동 폴백)
  const fetchChats = async () => {
    if (!userId) {
      setChats([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    try {
      const { data, error } = await supabase.rpc('get_my_chat_list');

      if (error || !Array.isArray(data)) {
        console.warn('[ChatList] RPC 실패 → 기존 조회 경로로 폴백', error);
        return await fetchChatsLegacy();
      }

      const formattedData = adaptRpcChatList(data, userId, t, myLanguage);
      setChats(formattedData);
      setIsLoading(false);
      if (Array.isArray(formattedData) && formattedData.length > 0) {
        setListCache(userId, 'chat_list', formattedData);
      }
    } catch (err) {
      console.warn('[ChatList] RPC 변환 예외 발생 → 기존 조회 경로로 폴백', err);
      return await fetchChatsLegacy();
    }
  };

  useEffect(() => {
    fetchChats();
    checkGroupNotifications(userId);
  }, [userId, myLanguage]);

  // [Preload] 브라우저 유휴 시 ChatConversation 컴포넌트 미리 불러오기
  useEffect(() => {
    const preload = () => {
      import('../ChatConversation/ChatConversation').catch(() => {});
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(preload, { timeout: 2000 });
      return () => window.cancelIdleCallback(handle);
    } else {
      const timer = setTimeout(preload, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // [4-B] 내 초대 및 참여 상태 실시간 구독 (participants INSERT/UPDATE)
  useEffect(() => {
    if (!userId) return;

    const channel = supabase.channel('chat-list-participants-' + userId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'participants',
          filter: 'profile_id=eq.' + userId
        },
        () => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(() => {
            fetchChats();
            checkGroupNotifications(userId);
          }, 300);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'participants',
          filter: 'profile_id=eq.' + userId
        },
        () => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(() => {
            fetchChats();
            checkGroupNotifications(userId);
          }, 300);
        }
      )
      .subscribe();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // [4-C] 메시지 실시간 수신 구독 (messages INSERT / UPDATE) - 안읽음 개수·시간·정렬 및 번역문 실시간 갱신
  useEffect(() => {
    if (!userId) return;

    const channel = supabase.channel('chat-list-messages-' + userId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        () => {
          if (messagesDebounceTimerRef.current) {
            clearTimeout(messagesDebounceTimerRef.current);
          }
          messagesDebounceTimerRef.current = setTimeout(() => {
            fetchChats();
            checkGroupNotifications(userId);
          }, 500);
        }
      )
      // 2026-09-11: 번역문은 INSERT 이후 UPDATE 로 채워지므로 UPDATE 도 청취
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        },
        () => {
          if (messagesDebounceTimerRef.current) {
            clearTimeout(messagesDebounceTimerRef.current);
          }
          messagesDebounceTimerRef.current = setTimeout(() => {
            fetchChats();
            checkGroupNotifications(userId);
          }, 500);
        }
      )
      .subscribe();

    return () => {
      if (messagesDebounceTimerRef.current) {
        clearTimeout(messagesDebounceTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // 알림 변경 신호기(notifSignal) 수신 시 단체 알림 상태 재확인 (초기값 0 중복 실행 방지)
  useEffect(() => {
    if (!userId) return;
    if (prevNotifSignalRef.current !== notifSignal) {
      prevNotifSignalRef.current = notifSignal;
      checkGroupNotifications(userId);
    }
  }, [notifSignal, userId]);

  // [4-D] 프로필 변경 실시간 구독 (profiles UPDATE) - 상대방 국기/이름/아바타 변경 실시간 갱신
  useEffect(() => {
    if (!userId) return;

    const channel = supabase.channel('chat-list-profiles-' + userId)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          const updatedProfile = payload.new;
          if (!updatedProfile || !updatedProfile.id) return;

          // 진단용 임시 로그 (위치: ChatList.jsx profiles UPDATE 수신 콜백)
          console.log('[ChatList Realtime] profiles UPDATE 수신:', {
            id: updatedProfile.id,
            flag_code: updatedProfile.flag_code,
            name: updatedProfile.name,
            nickname: updatedProfile.nickname
          });

          // 현재 내 채팅 목록에 포함된 상대방인지 검사 (본인 프로필이거나 상대방인 경우만 처리)
          const isRelevant =
            updatedProfile.id === userId ||
            (chatsRef.current || []).some(
              (c) =>
                c.partnerId === updatedProfile.id ||
                (Array.isArray(c.otherProfileIds) && c.otherProfileIds.includes(updatedProfile.id))
            );

          if (!isRelevant) return;

          if (updatedProfile.id === userId) {
            if (typeof updatedProfile.name_set === 'boolean') {
              setNameSet(updatedProfile.name_set);
            } else {
              fetchMyProfile(userId);
            }
          }

          if (profilesDebounceTimerRef.current) {
            clearTimeout(profilesDebounceTimerRef.current);
          }
          profilesDebounceTimerRef.current = setTimeout(() => {
            fetchChats();
          }, 300);
        }
      )
      .subscribe((status) => {
        // 진단용 임시 로그 (위치: ChatList.jsx profiles subscribe 콜백)
        console.log('[ChatList Realtime] profiles 구독 상태:', status);
      });

    return () => {
      if (profilesDebounceTimerRef.current) {
        clearTimeout(profilesDebounceTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // [3-E 작업 7] 초대 수락 핸들러
  const handleAcceptInvite = async () => {
    if (!selectedInviteChat || isProcessingInvite) return;
    const targetChatId = selectedInviteChat.id;
    setIsProcessingInvite(true);
    let isSuccess = false;
    try {
      const { error } = await supabase.rpc('accept_group_invite', {
        p_conversation_id: targetChatId,
      });

      if (error) {
        console.error('Error accepting group invite:', error);
        setToastConfig({
          message: t('group_chat.invite_error', '초대 처리 중 오류가 발생했습니다.'),
          duration: 2500,
        });
      } else {
        isSuccess = true;
      }
    } catch (err) {
      console.error('Error accepting group invite:', err);
      setToastConfig({
        message: t('group_chat.invite_error', '초대 처리 중 오류가 발생했습니다.'),
        duration: 2500,
      });
    } finally {
      setSelectedInviteChat(null);
      setIsProcessingInvite(false);
      await fetchChats();
      if (isSuccess && targetChatId) {
        navigate(`/chat/${targetChatId}`, {
          state: {
            initialPartner: selectedInviteChat?.type === 'direct' ? selectedInviteChat?.partnerProfile : null,
            conversationType: selectedInviteChat?.type || 'direct',
          }
        });
      }
    }
  };

  // [3-E 작업 7] 초대 거절 핸들러
  const handleRejectInvite = async () => {
    if (!selectedInviteChat || isProcessingInvite) return;
    setIsProcessingInvite(true);
    try {
      const { error } = await supabase.rpc('reject_group_invite', {
        p_conversation_id: selectedInviteChat.id,
      });

      if (error) {
        console.error('Error rejecting group invite:', error);
        setToastConfig({
          message: t('group_chat.invite_error', '초대 처리 중 오류가 발생했습니다.'),
          duration: 2500,
        });
      } else {
        setToastConfig({
          message: t('group_chat.invite_rejected_toast', '대화방 초대를 거절했습니다.'),
          duration: 2500,
        });
      }
    } catch (err) {
      console.error('Error rejecting group invite:', err);
      setToastConfig({
        message: t('group_chat.invite_error', '초대 처리 중 오류가 발생했습니다.'),
        duration: 2500,
      });
    } finally {
      setSelectedInviteChat(null);
      setIsProcessingInvite(false);
      await fetchChats();
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedIds.length === 0 || !userId) return;

    const targets = chats.filter(chat => selectedIds.includes(chat.id));
    const friendTargets = targets.filter(t => Boolean(t.partnerId));
    const partnerIds = Array.from(new Set(friendTargets.map(t => t.partnerId)));

    try {
      if (friendTargets.length > 0) {
        const backupPayload = friendTargets.map(target => ({
          user_id: userId,
          friend_id: target.partnerId,
          friend_name: target.name || 'Unknown',
          friend_flag_code: target.flagCode || 'un',
          friend_language: target.language || 'en'
        }));

        const { error: backupError } = await supabase
          .from('deleted_friends')
          .upsert(backupPayload, { onConflict: 'user_id,friend_id' });

        if (backupError) throw backupError;
      }

      if (partnerIds.length > 0) {
        const { error: friendError } = await supabase
          .from('friendships')
          .delete()
          .eq('user_id', userId)
          .in('friend_id', partnerIds);

        if (friendError) throw friendError;
      }

      const { error: partError } = await supabase
        .from('participants')
        .update({ hidden: true })
        .eq('profile_id', userId)
        .in('conversation_id', selectedIds);

      if (partError) throw partError;

      // 1:1 대화방 숨김/나가기 서버 처리 성공 직후 채팅 목록 사본 폐기
      invalidateListCache('chat_list');
      // 1:1 대화방 삭제 시 friendships 관계도 함께 삭제되므로 친구 목록 사본 폐기
      invalidateListCache('friend_list');

      setSelectedIds([]);
      setIsEditMode(false);
      setIsDeleteConfirmOpen(false);
      setToastConfig({ message: t('chat_list.toast_deleted'), duration: 2500 });
      fetchChats();
    } catch (error) {
      console.error('완전 삭제 실패:', error);
      alert(t('chat_list.error_delete'));
    }
  };

  const handleConfirmBlock = async () => {
    if (selectedIds.length === 0 || !userId) return;

    const targets = chats.filter(chat => selectedIds.includes(chat.id));
    const partnerIds = targets.map(t => t.partnerId).filter(Boolean);

    try {
      if (partnerIds.length > 0) {
        const { error } = await supabase.rpc('block_friends', { p_ids: partnerIds });

        if (error) throw error;
      }

      setSelectedIds([]);
      setIsEditMode(false);
      setIsBlockConfirmOpen(false);
      setToastConfig({ message: t('chat_list.toast_blocked'), duration: 2500 });
      fetchChats();
    } catch (error) {
      console.error('차단 실패:', error);
      alert(t('chat_list.error_block'));
    }
  };

  return (
    <div className="bg-[#FFFDF5] text-on-surface font-body-md flex-1 flex flex-col relative pb-[80px] overflow-x-hidden">
      {/* TopAppBar */}
      <header className="bg-surface-bright shadow-sm docked full-width top-0 z-40 sticky">
        <div className="flex justify-between items-center px-md py-sm w-full relative">
          <div className="flex items-center gap-2 pl-4">
            <div className="w-[54px] h-[54px] flex items-center justify-center">
              <Logo 
                className="w-full h-full object-contain cursor-pointer transition-transform hover:scale-105 active:scale-95" 
                onClick={() => navigate('/new-chat')}
              />
            </div>
            <button
              type="button"
              onClick={() => setToastConfig({ message: t('chat_list.toast_current_direct'), duration: 1500 })}
              onContextMenu={(e) => e.preventDefault()}
              className="font-headline-sm text-[20px] font-bold text-primary tracking-tight leading-tight select-none cursor-pointer"
            >
              {t('chat_list.title')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/group-chats')}
              onContextMenu={(e) => e.preventDefault()}
              className="relative text-[20px] font-bold text-primary/50 bg-primary/8 hover:bg-primary/12 shadow-sm rounded-full px-2 py-0 transition-colors active:scale-95 flex items-center justify-center cursor-pointer leading-tight select-none"
            >
              {t('chat_list.tab_group', '단체')}
              {hasGroupNotification && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-500 pointer-events-none animate-pulse" />
              )}
            </button>
          </div>
          <div className="relative flex items-center">
            <button
              onClick={() => {
                if (isEditMode) {
                  setIsEditMode(false);
                  setSelectedIds([]);
                  setIsMenuOpen(false);
                } else {
                  setIsMenuOpen((prev) => !prev);
                }
              }}
              className="p-2 rounded-full hover:bg-surface-container transition-colors active:scale-95 flex items-center justify-center"
              aria-label={t('chat_list.manage_aria')}
            >
              <span
                className={`material-symbols-outlined text-[24px] transition-colors ${
                  isEditMode ? 'text-primary' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {isEditMode ? 'close' : 'more_vert'}
              </span>
            </button>

            {/* Dropdown Backdrop */}
            {isMenuOpen && !isEditMode && (
              <div
                className="fixed inset-0 z-40 bg-transparent"
                onClick={() => setIsMenuOpen(false)}
              />
            )}

            {/* Dropdown Menu */}
            {isMenuOpen && !isEditMode && (
              <div className="absolute right-0 top-12 z-50 min-w-[190px] bg-surface-container-lowest rounded-2xl shadow-lg border-2 border-[#006565] py-1 origin-top-right animate-in fade-in zoom-in-95 duration-150 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditMode(true);
                    setSelectedIds([]);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 py-2 px-4 text-left hover:bg-surface-container transition-colors active:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined text-[20px] text-on-surface">
                    tune
                  </span>
                  <span className="font-body-md text-[14px] font-medium text-on-surface">
                    {t('chat_list.menu_block_delete')}
                  </span>
                </button>
                <div className="h-[1px] bg-surface-container-high/60 mx-2" />
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsBlockedSheetOpen(true);
                  }}
                  className="w-full flex items-center gap-3 py-2 px-4 text-left hover:bg-surface-container transition-colors active:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined text-[20px] text-on-surface-variant/80">
                    block
                  </span>
                  <span className="font-body-md text-[14px] font-medium text-on-surface-variant/80">
                    {t('chat_list.menu_manage_blocked')}
                  </span>
                </button>
                <div className="h-[1px] bg-surface-container-high/60 mx-2" />
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsDeletedSheetOpen(true);
                  }}
                  className="w-full flex items-center gap-3 py-2 px-4 text-left hover:bg-surface-container transition-colors active:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined text-[20px] text-on-surface-variant/80">
                    restore_from_trash
                  </span>
                  <span className="font-body-md text-[14px] font-medium text-on-surface-variant/80">
                    {t('chat_list.menu_manage_deleted')}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* 임시 프로필 안내 띠 (name_set === false 인 경우만 노출) */}
      <TempProfileBanner nameSet={nameSet} />

      {/* 푸시 알림 켜기 안내 배너 */}
      <PushPromptBanner
        userId={userId}
        onShowToast={(msg) => setToastConfig({ message: msg })}
      />

      {/* Main Content Canvas - Chat List */}
      <main className={`flex-grow overflow-y-auto px-md py-md flex flex-col gap-0.5 ${isBlockedSheetOpen || isDeletedSheetOpen ? 'pb-[55vh]' : 'pb-28'}`}>
        {/* Search Bar + Notification Bell */}
        <div className="flex items-center gap-2 mb-2 w-full">
          <div className="flex-1 bg-surface-container-low rounded-xl px-3 h-8 flex items-center gap-sm border border-surface-container-high">
            <span className="material-symbols-outlined text-outline text-[15px]">search</span>
            <input
              className="bg-transparent border-none focus:ring-0 text-on-surface w-full font-body-sm placeholder:text-outline-variant outline-none"
              placeholder={t('chat_list.search_placeholder')}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => navigate('/notifications')}
            className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-xl hover:bg-surface-container active:scale-95 transition-all cursor-pointer"
            aria-label={t('notification_box.bell_aria', '알림 보관함')}
          >
            <span
              className={`w-[28px] h-[28px] relative inline-flex items-center justify-center ${hasUnreadNotifications ? 'bell-ring' : ''}`}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className={`block transition-colors ${hasUnreadNotifications ? 'text-[#00B248]' : 'text-outline'}`} aria-hidden="true">
                <path d={hasUnreadNotifications ? 'M4 19v-2h2v-7q0-2.075 1.25-3.687T10.5 4.2v-.7q0-.625.438-1.062T12 2t1.063.438T13.5 3.5v.7q2 .5 3.25 2.113T18 10v7h2v2zm8 3q-.825 0-1.412-.587T10 20h4q0 .825-.587 1.413T12 22' : 'M4 19v-2h2v-7q0-2.075 1.25-3.687T10.5 4.2v-.7q0-.625.438-1.062T12 2t1.063.438T13.5 3.5v.7q2 .5 3.25 2.113T18 10v7h2v2zm8 3q-.825 0-1.412-.587T10 20h4q0 .825-.587 1.413T12 22m-4-5h8v-7q0-1.65-1.175-2.825T12 6T9.175 7.175T8 10z'} />
              </svg>
            </span>
          </button>
        </div>

        {/* Chat List Items */}
        {isLoading && chats.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-on-surface-variant text-center py-10">
            <span className="material-symbols-outlined text-[32px] mb-2 animate-spin opacity-50">progress_activity</span>
            <p className="font-body-md">{t('chat_list.loading')}</p>
          </div>
        ) : chats.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-on-surface-variant text-center py-10 px-4">
            <div className="w-16 h-16 rounded-full bg-surface-container-low flex items-center justify-center mb-4 shadow-sm">
              <span className="material-symbols-outlined text-[32px] text-primary opacity-80">chat_bubble</span>
            </div>
            <p className="font-headline-sm text-[16px] font-bold text-on-surface mb-1">
              {t('chat_list.empty_title', '아직 대화가 없어요')}
            </p>
            <p className="font-body-sm text-on-surface-variant text-[13px] mb-6 max-w-[220px]">
              {t('chat_list.empty_subtitle', '새 대화를 시작해보세요.')}
            </p>
            <button
              type="button"
              onClick={() => navigate('/new-chat')}
              className="w-full max-w-[240px] justify-center bg-primary text-on-primary font-headline-sm text-[14px] font-semibold px-6 py-2.5 rounded-full shadow-md hover:bg-primary/90 active:scale-95 transition-all duration-200 flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              {t('chat_list.start_new_chat', '새 대화 시작')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/find-friend')}
              className="mt-3 w-full max-w-[240px] justify-center bg-transparent border border-primary text-primary font-headline-sm text-[14px] font-semibold px-6 py-2.5 rounded-full hover:bg-primary/10 active:scale-95 transition-all duration-200 flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[18px]">person_search</span>
              {t('chat_list.find_friend_by_email')}
            </button>
          </div>
        ) : (
          chats
            .filter((chat) => chat.type === 'direct')
            .filter((chat) =>
              (chat.name || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
            )
            .map((chat) => {
            const isSelected = selectedIds.includes(chat.id);

            const itemInner = (
              <>
                {isEditMode && (
                  <div className="flex-shrink-0 flex items-center justify-center">
                    <span
                      className={`material-symbols-outlined text-[22px] ${isSelected ? 'text-primary' : 'text-outline-variant'}`}
                      style={{ fontVariationSettings: isSelected ? "'FILL' 1" : "'FILL' 0" }}
                    >
                      {isSelected ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                  </div>
                )}
                {/* Avatar */}
                {chat.type === 'group' ? (
                  <div className="relative flex-shrink-0 flex items-center h-8">
                    {/* [보존] 기존 목록 아바타 스택 - 방 정보 화면의 멤버 목록으로 재활용 예정
                    {(!chat.avatars || chat.avatars.length === 0) ? (
                      <div className="w-8 h-8 flex items-center justify-center shrink-0 aspect-square">
                        <img
                          src={resolveAvatarUrl(null)}
                          alt="avatar"
                          className="w-full h-full rounded-full object-cover shadow-sm border border-outline-variant/20"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center" style={{ width: `${Math.min(chat.avatars.length, 3) * 16 + 16}px` }}>
                        {chat.avatars.map((av, idx) => (
                          <img
                            key={idx}
                            src={av}
                            alt={`avatar-${idx}`}
                            className="w-7 h-7 rounded-full object-cover shadow-sm border border-surface-container-lowest"
                            style={{
                              marginLeft: idx === 0 ? '0px' : '-10px',
                              zIndex: 10 - idx,
                            }}
                          />
                        ))}
                      </div>
                    )}
                    */}
                    <div className="relative w-7 h-7 flex-shrink-0 flex items-center justify-center">
                      <img
                        src="/abata/group_abata.webp"
                        alt="group-avatar"
                        className="w-full h-full rounded-full object-cover"
                      />
                      {typeof chat.memberCount === 'number' && !isNaN(chat.memberCount) && chat.memberCount > 0 && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span
                            className={`font-bold text-on-surface leading-none ${
                              chat.memberCount >= 10 ? 'text-[11px]' : 'text-[14px]'
                            }`}
                            style={{
                              textShadow: '0 0 2px #fff, 0 0 4px #fff, -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff'
                            }}
                          >
                            {chat.memberCount}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="relative flex-shrink-0 shrink-0 aspect-square">
                    <div className="w-7 h-7 flex items-center justify-center shrink-0 aspect-square">
                      <Avatar
                        userId={chat.partnerId}
                        name={chat.name}
                        avatarUrl={chat.avatar}
                        avatarSelected={chat.avatarSelected ?? false}
                        size="w-7 h-7"
                        className="shadow-sm border border-outline-variant/20"
                      />
                    </div>
                    {chat.flagCode && (
                      <div className="absolute -bottom-1 -right-1 bg-surface-container-lowest rounded-full p-[2px] shadow-sm flex items-center justify-center">
                        <FlagIcon code={chat.flagCode} className="w-4 h-3 rounded-full object-cover" />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex-grow min-w-0 flex flex-col justify-center">
                  <div className="flex items-center min-w-0">
                    <h3 className="font-headline-sm text-[14px] font-semibold text-on-surface truncate min-w-0 flex items-center gap-1.5 leading-tight">
                      <span className="truncate">{chat.name}</span>
                      {/* [보존] 아바타 중앙 인원수로 대체됨
                      {chat.type === 'group' && chat.memberCount > 1 && (
                        <span className="text-on-surface-variant text-[12px] font-normal flex-shrink-0">
                          {chat.memberCount}
                        </span>
                      )}
                      */}
                      {chat.myStatus === 'pending' && (
                        <span className="bg-primary/15 text-primary text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                          {t('chat_list.invited_badge', '초대')}
                        </span>
                      )}
                      {chat.isBlocked && (
                        <span className="bg-surface-variant text-on-surface-variant text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                          {t('chat_list.blocked_badge', '차단됨')}
                        </span>
                      )}
                    </h3>
                  </div>
                  {!chat.isBlocked && chat.lastMessage && (
                    <p className={`text-[11.5px] line-clamp-2 leading-[1.2] break-words pr-6 ${chat.myStatus === 'pending' ? 'text-outline' : 'text-on-surface-variant'}`}>
                      {chat.lastMessage}
                    </p>
                  )}
                </div>
                <div className="flex items-center ml-auto flex-shrink-0 shrink-0">
                  {chat.hasMessages && chat.unreadCount > 0 && !chat.isBlocked && (
                    <span className="mr-1.5 flex items-center gap-1">
                      <span className="min-w-[18px] h-[18px] rounded-full bg-primary/85 text-white text-[11px] font-medium flex-items-center justify-center px-1.5">
                        {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                      </span>
                    </span>
                  )}
                  <span className={`w-[54px] flex flex-col items-end justify-center text-[11px] ${chat.unread ? 'text-primary font-bold' : 'text-outline font-semibold'} mr-3`}>
                    <span>{chat.dateText}</span>
                    {chat.timeText && (
                      <span className="text-[9px] text-outline/80 font-normal">{chat.timeText}</span>
                    )}
                  </span>
                  {!isEditMode && (
                    <button
                      onClick={(e) => toggleFavorite(e, chat.partnerId)}
                      className="p-1 -my-1 rounded-full hover:bg-surface-container transition-colors flex items-center justify-center active:scale-90"
                    >
                      <span
                        className={`material-symbols-outlined text-[18px] ${favorites.includes(chat.partnerId) ? 'text-primary' : 'text-outline-variant hover:text-on-surface'}`}
                        style={{ fontVariationSettings: favorites.includes(chat.partnerId) ? "'FILL' 1" : "'FILL' 0" }}
                      >
                        favorite
                      </span>
                    </button>
                  )}
                </div>
              </>
            );

            const itemClassName = "bg-surface-container-lowest rounded-xl px-2 py-3 flex items-center gap-3 custom-shadow hover:bg-surface-container-low transition-colors cursor-pointer border border-transparent hover:border-outline-variant/30 min-h-[54px]";

            if (isEditMode) {
              return (
                <div
                  key={chat.id}
                  onClick={() => {
                    setSelectedIds((prev) =>
                      prev.includes(chat.id)
                        ? prev.filter((id) => id !== chat.id)
                        : [...prev, chat.id]
                    );
                  }}
                  className={itemClassName}
                >
                  {itemInner}
                </div>
              );
            }

            return (
              <div 
                key={chat.id} 
                onClick={(e) => {
                  if (chat.myStatus === 'pending') {
                    e.preventDefault();
                    setSelectedInviteChat(chat);
                  } else if (chat.isBlocked) {
                    e.preventDefault();
                    setIsBlockedSheetOpen(true);
                  } else {
                    navigate(`/chat/${chat.id}`, {
                      state: {
                        initialPartner: chat.type === 'direct' ? chat.partnerProfile : null,
                        conversationType: chat.type,
                      }
                    });
                  }
                }}
                className={itemClassName}
              >
                {itemInner}
              </div>
            );
          })
        )}
      </main>

      {/* Bottom Action Bar (편집 모드에서 1개 이상 선택 시 노출) - 정렬/애니메이션 책임 2층 분리 */}
      {isEditMode && selectedIds.length > 0 && (
        <div className="fixed bottom-[90px] w-[calc(100%-32px)] max-w-[448px] left-1/2 -translate-x-1/2 z-50">
          <style>{`
            @keyframes slideUpCapsuleOnly {
              0% { opacity: 0; transform: translateY(28px) scale(0.96); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
          <div
            onClick={() => setSelectedIds([])}
            className="w-full bg-surface-bright border border-outline-variant/40 rounded-2xl px-5 py-3.5 shadow-floating-strong flex items-center justify-between"
            style={{ animation: 'slideUpCapsuleOnly 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
          >
            <span className="font-headline-sm text-on-surface font-bold text-[15px] pl-1">
              {t('chat_list.selected_count', { count: selectedIds.length })}
            </span>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => {
                  if (selectedIds.length === 0) return;
                  setIsBlockConfirmOpen(true);
                }}
                className="bg-surface-container-highest text-on-surface font-semibold text-[14px] px-5 py-2.5 rounded-full hover:bg-outline-variant/30 active:scale-95 transition-colors"
              >
                {t('chat_list.action_block')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (selectedIds.length === 0) return;
                  setIsDeleteConfirmOpen(true);
                }}
                className="bg-[#E53935] text-white font-semibold text-[14px] px-5 py-2.5 rounded-full shadow-md hover:bg-[#D32F2F] active:scale-95 transition-colors"
              >
                {t('chat_list.action_delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button Backdrop */}
      {isFabMenuOpen && !isEditMode && (
        <div
          className="fixed inset-0 z-40 bg-black/20 animate-in fade-in duration-150"
          onClick={() => setIsFabMenuOpen(false)}
        />
      )}

      {/* Floating Action Button & Popup Menu */}
      {!isEditMode && (
        <div className="fixed bottom-[90px] w-full max-w-[480px] left-1/2 -translate-x-1/2 pointer-events-none z-40">
          <div className="absolute right-md bottom-0 pointer-events-auto flex flex-col items-end gap-2">
            {/* 2분기 팝업 메뉴 */}
            {isFabMenuOpen && (
              <div className="mb-2 bg-surface-container-lowest rounded-2xl shadow-xl border-2 border-primary py-1 min-w-[190px] origin-bottom-right animate-in fade-in zoom-in-95 duration-150 overflow-hidden">
                {/* 0) 이메일로 친구 찾기 (/find-friend 경로) */}
                <button
                  type="button"
                  onClick={() => {
                    setIsFabMenuOpen(false);
                    navigate('/find-friend');
                  }}
                  className="w-full flex items-center gap-3 py-2.5 px-4 text-left hover:bg-surface-container transition-colors active:bg-surface-container-high cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[20px] text-primary">
                    person_search
                  </span>
                  <span className="font-headline-sm text-[14px] font-semibold text-on-surface">
                    {t('chat_list.find_friend_by_email')}
                  </span>
                </button>

                <div className="h-[1px] bg-surface-container-high/60 mx-2" />

                {/* 1) 새 친구 초대 (/new-chat 경로 100% 동일) */}
                <button
                  type="button"
                  onClick={() => {
                    setIsFabMenuOpen(false);
                    navigate('/new-chat');
                  }}
                  className="w-full flex items-center gap-3 py-2.5 px-4 text-left hover:bg-surface-container transition-colors active:bg-surface-container-high cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[20px] text-primary">
                    person_add
                  </span>
                  <span className="font-headline-sm text-[14px] font-semibold text-on-surface">
                    {t('chat_list.fab_menu_invite')}
                  </span>
                </button>

                <div className="h-[1px] bg-surface-container-high/60 mx-2" />

                {/* 2) 단체 대화방 만들기 (/new-group-chat 경로) */}
                <button
                  type="button"
                  onClick={() => {
                    setIsFabMenuOpen(false);
                    navigate('/new-group-chat');
                  }}
                  className="w-full flex items-center gap-3 py-2.5 px-4 text-left hover:bg-surface-container transition-colors active:bg-surface-container-high cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[20px] text-primary">
                    group_add
                  </span>
                  <span className="font-headline-sm text-[14px] font-semibold text-on-surface">
                    {t('chat_list.fab_menu_group')}
                  </span>
                </button>
              </div>
            )}

            {/* 메인 FAB 버튼 */}
            <button
              type="button"
              onClick={() => setIsFabMenuOpen(prev => !prev)}
              className={`group h-[56px] bg-[#4CAF50] text-white rounded-full flex items-center shadow-[0_4px_14px_rgba(76,175,80,0.4)] active:scale-95 transition-all duration-300 ease-in-out overflow-hidden cursor-pointer ${
                isFabExpanded ? 'max-w-[140px] px-5' : 'max-w-[56px] px-4 hover:max-w-[140px] hover:px-5'
              }`}
              aria-label={t('chat_list.fab_new_chat')}
            >
              <span
                className={`material-symbols-outlined shrink-0 text-[24px] transition-transform duration-300 ${
                  isFabMenuOpen ? 'rotate-45' : 'rotate-0'
                }`}
                style={{ fontVariationSettings: "'FILL' 0" }}
              >
                add
              </span>
              <span
                className={`font-body-md text-[16px] font-semibold whitespace-nowrap transition-all duration-300 ease-in-out ${
                  isFabExpanded ? 'opacity-100 ml-1.5' : 'opacity-0 ml-0 group-hover:opacity-100 group-hover:ml-1.5'
                }`}
              >
                {t('chat_list.fab_new_chat')}
              </span>
            </button>
          </div>
        </div>
      )}

      <ConfirmSheet
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title={t('chat_list.delete_sheet_title')}
        description={[
          t('chat_list.delete_sheet_desc1', { count: selectedIds.length }),
          t('chat_list.delete_sheet_desc2'),
          t('chat_list.delete_sheet_desc3')
        ]}
        confirmLabel={t('chat_list.delete_sheet_title')}
        cancelLabel={t('chat_list.cancel')}
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
      />

      <ConfirmSheet
        isOpen={isBlockConfirmOpen}
        onClose={() => setIsBlockConfirmOpen(false)}
        title={t('chat_list.block_sheet_title')}
        description={[
          t('chat_list.block_sheet_desc1', { count: selectedIds.length }),
          t('chat_list.block_sheet_desc2'),
          t('chat_list.block_sheet_desc3')
        ]}
        confirmLabel={t('chat_list.action_block')}
        cancelLabel={t('chat_list.cancel')}
        confirmVariant="primary"
        onConfirm={handleConfirmBlock}
      />

      {/* [3-E 작업 6] 초대 수락/거절 ConfirmSheet */}
      <ConfirmSheet
        isOpen={!!selectedInviteChat}
        onClose={() => {
          if (!isProcessingInvite) {
            setSelectedInviteChat(null);
          }
        }}
        title={t('group_chat.invite_sheet_title', '단체 대화방 초대')}
        description={[
          selectedInviteChat?.name || t('group_chat.header_default_name', '단체 대화방'),
          t('group_chat.invite_sheet_desc', '대화방 초대를 수락하시겠습니까?')
        ]}
        confirmLabel={t('group_chat.accept', '수락')}
        cancelLabel={t('group_chat.reject', '거절')}
        confirmVariant="primary"
        onConfirm={handleAcceptInvite}
        onCancel={handleRejectInvite}
        disabled={isProcessingInvite}
      />

      <BlockedFriendsSheet
        isOpen={isBlockedSheetOpen}
        onClose={() => setIsBlockedSheetOpen(false)}
        onShowToast={(message) => setToastConfig({ message, duration: 2500 })}
        onUnblock={() => {
          fetchChats();
          fetchFavorites(userId);
        }}
      />

      <DeletedFriendsSheet
        isOpen={isDeletedSheetOpen}
        onClose={() => setIsDeletedSheetOpen(false)}
        onShowToast={(message) => setToastConfig({ message, duration: 2500 })}
        onRestore={() => {
          fetchChats();
          fetchFavorites(userId);
        }}
      />

      <Toast
        message={toastConfig?.message}
        duration={toastConfig?.duration}
        onClose={() => setToastConfig(null)}
      />

      <BottomNav />
    </div>
  );
}
